import { Button, Col, Form, Grid, Input, Row, Typography } from "antd";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  validateEmail,
  validateUsername,
} from "../../../components/apiRepository/DynamodbRepository";
import BrandButton from "../../../components/v2/Buttons/BrandButton";
import accountTypes from "../../../constants/accountTypes";
import {
  ACTIONTYPES,
  useStore,
  useStoreDispatch,
} from "../../../context/CreateStoreContext";
import useComponentSize from "../../../hooks/useComponentSize";

const { Title, Paragraph } = Typography;

export default function AccountInformation({ proceed }) {
  const { accountType, account } = useStore();
  const [isChild, setIsChild] = useState(false);
  const navigate = useNavigate();
  const dispatch = useStoreDispatch();
  const [form] = Form.useForm();
  const submitButtonRef = useRef();
  const [loading, setLoading] = useState(false);
  const componentSize = useComponentSize();

  const setFormError = (fieldName, errorMessage) => {
    form.setFields([
      {
        name: fieldName,
        errors: [errorMessage],
      },
    ]);
  };

  const onFinish = async (values) => {
    try {
      setLoading(true);
      const { emailAddress } = values;

      const validationFunction = isChild ? validateUsername : validateEmail;
      const validationParam = isChild
        ? { username: emailAddress }
        : { emailAddress };

      const response = await validationFunction(validationParam);

      const existsKey = isChild ? "usernameExists" : "emailExists";
      const existsMessage = isChild
        ? "Username already exists."
        : "Email address already exists.";

      if (response.data[existsKey]) {
        setFormError("emailAddress", existsMessage);
        return;
      }

      dispatch({ type: ACTIONTYPES.ACCOUNT, data: values });
      proceed();
    } catch (error) {
      setFormError(
        "emailAddress",
        `Error in ${
          isChild ? "username" : "email address"
        }. Please fix and try again.`,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setIsChild(() => accountType === accountTypes.store);
  }, [accountType]);

  return (
    <Row className="BevzForm">
      <Col
        xs={{ span: 22, offset: 1 }}
        lg={{ span: 20, offset: 2 }}
        xl={{ span: 15, offset: 2 }}
      >
        <Title
          level={componentSize === "default" ? 4 : 2}
          className="BevzFormHeader"
        >
          Account Information
        </Title>
        <Paragraph className="BevzFormSubHeader">
          Provide your email and create a secure password to set up your
          account.
        </Paragraph>

        <Form
          initialValues={account}
          form={form}
          onFinish={onFinish}
          layout="vertical"
          requiredMark={false}
          style={{ marginTop: "2.5rem", width: "100%" }}
          wrapperCol={{ span: 24 }}
        >
          <Form.Item
            name="emailAddress"
            label={
              <>
                {isChild ? "Username" : "Email Address"}
                <span style={{ color: "red" }}>*</span>
              </>
            }
            required
            rules={
              isChild
                ? [{ required: true, message: "This is a required field." }]
                : [
                    { required: true, message: "This is a required field." },
                    {
                      type: "email",
                      message: "Please enter valid email address.",
                    },
                  ]
            }
          >
            <Input placeholder={isChild ? "" : "your@email.com"} size="large" />
          </Form.Item>
          <Row gutter={[30, 8]}>
            <Col xs={24} md={12}>
              <Form.Item
                hasFeedback
                name="password"
                label={
                  <>
                    Password<span style={{ color: "red" }}>*</span>
                  </>
                }
                required
                rules={[
                  { required: true, message: "This is a required field." },
                  {
                    min: 8,
                    message: "Password must be at least 8 characters.",
                  },
                ]}
              >
                <Input.Password placeholder="" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                dependencies={["password"]}
                hasFeedback
                name="confirmpassword"
                label={
                  <>
                    Confirm Password<span style={{ color: "red" }}>*</span>
                  </>
                }
                required
                rules={[
                  { required: true, message: "This is a required field." },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("Password does not match!"),
                      );
                    },
                  }),
                ]}
              >
                <Input.Password placeholder="" size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Row
            align="middle"
            justify="space-between"
            className="BevzFormButtonSection"
          >
            <Col>
              <Form.Item>
                <Button
                  type="default"
                  size="large"
                  shape="round"
                  onClick={() => navigate(isChild ? "/dashboard" : "/login")}
                >
                  Cancel
                </Button>
              </Form.Item>
            </Col>
            <Col>
              <Form.Item>
                <BrandButton
                  ref={submitButtonRef}
                  htmlType="submit"
                  type="solidyellow"
                  loading={loading}
                  size="large"
                >
                  {componentSize === "default"
                    ? "Proceed"
                    : "Proceed to Store Information"}
                </BrandButton>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Col>
    </Row>
  );
}
